import { useState, useContext, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

import MUICard from "./../UI/MUICard/MUICard";
import classes from "./AIGenerated.module.css";
import MUIIconButton from "../UI/MUIIconButton/MUIIconButton";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import api from "../../BrandOnBoarding/api";

function calculateRows(text, textareaCols) {
  const lines = text.split("\n");
  let totalRows = 0;
  lines.forEach((line) => {
    totalRows += Math.ceil(line.length / textareaCols);
  });
  return totalRows + 1;
}

export function AILabel({ edit, isEditing, onEdit, loading, isMonitoring }) {
  return (
    <span className={classes.sub_title}>
      <span>Summary</span>
      {edit && !loading && !isMonitoring && (
        <MUIIconButton
          onClick={onEdit}
          className={classes.edit_btn}
          data-html2canvas-ignore
        >
          {!isEditing && <DriveFileRenameOutlineOutlinedIcon />}
          {isEditing && <DoneOutlinedIcon />}
        </MUIIconButton>
      )}
    </span>
  );
}

export default function AIGenerated({
  loading,
  text,
  children,
  firebaseNode,
  edit = false,
  isMonitoring = false,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [summary, setSummary] = useState(text);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  const discoveryCtx = useContext(DiscoveryContext);

  useEffect(() => {
    setSummary(text);
    setIsExpanded(false);
  }, [text]);

  useEffect(() => {
    if (textRef.current && !loading && !isExpanded) {
      setIsClamped(
        textRef.current.scrollHeight > textRef.current.clientHeight + 1
      );
    }
  }, [text, loading, isExpanded]);

  function LoadingSkeletion() {
    const skeletionWidths = [100, 70, 90, 90, 70, 60];
    return (
      <div className="mt-3">
        {skeletionWidths.map((width, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={`${width}%`}
            sx={{ fontSize: 10 }}
          />
        ))}
      </div>
    );
  }

  async function handleEditing() {
    setIsEditing(!isEditing);
    if (isEditing) {
      if (summary.toString().trim() === text.toString().trim()) return;

      if (firebaseNode === "core_attributes" || firebaseNode === "category_dimensions" || firebaseNode === "taxonomies") {
        const typeMap = {
          core_attributes: "core-attribute",
          category_dimensions: "categoryDimension",
          taxonomies: "taxonomy",
        };
        const requestId = firebaseNode === "core_attributes"
          ? discoveryCtx.core_attributes?.products?.find(
              (p) => (p.brandName || p.name)?.toLowerCase() === discoveryCtx.entity?.toLowerCase()
            )?.request_id
          : firebaseNode === "taxonomies"
          ? discoveryCtx.taxonomies?.request_id
          : discoveryCtx.category_dimensions?.request_id;

        try {
          await api.put(
            `${process.env.REACT_APP_ORCHESTRATOR_BASE_URL}/update-summary`,
            {
              transaction_id: discoveryCtx.state.transactionId,
              request_id: requestId,
              summary,
              type: typeMap[firebaseNode],
            }
          );
        } catch (err) {
          console.error("Failed to update summary:", err);
        }
      }
      if (firebaseNode === "core_attributes") {
        discoveryCtx.updateCoreAttributes({
          ...discoveryCtx.core_attributes,
          summary,
        });
      } else if (firebaseNode === "taxonomies") {
        discoveryCtx.updatedTaxonomies({
          ...discoveryCtx.taxonomies,
          data: {
            ...discoveryCtx.taxonomies?.data,
            summary,
          },
        });
      } else {
        discoveryCtx.updateCategoryDimensions({
          ...discoveryCtx.category_dimensions,
          summary,
        });
      }
    }
  }

  return (
    <MUICard
      subText={
        <div className={classes.ai_label_wrapper}>
          <AILabel
            edit={edit}
            isEditing={isEditing}
            onEdit={handleEditing}
            loading={loading}
            isMonitoring={isMonitoring}
          />
        </div>
      }
      className={classes.container}
      variant="elevated"
      elevation={3}
    >
      <div className={classes.content}>
        {!loading && !isEditing && (
          <>
            <div
              ref={textRef}
              className={`${classes.text} ${!isExpanded ? classes.text_clamped : ""}`}
            >
              {text}
            </div>
            {(isClamped || isExpanded) && (
              <button
                className={classes.read_more_btn}
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? "Read Less ▲" : "Read More ▼"}
              </button>
            )}
          </>
        )}
        {loading && <LoadingSkeletion />}
        {children}
        {!loading && isEditing && (
          <textarea
            className={classes.text_area}
            cols={100}
            rows={calculateRows(text, 100)}
            onChange={(e) => setSummary(e.target.value)}
            value={summary}
          ></textarea>
        )}
      </div>
    </MUICard>
  );
}